import { Badge, Box, Button, Typography } from '@saviynt/design-system';
import cx from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import { TOKENS } from '../../../../shared/constants';

import { TRIGGER_LABELS, type TriggerType } from './TriggerSelectContent';

export type PolicyFormStep = 1 | 2;

interface PolicyFormStepsProps {
  selectedStep: PolicyFormStep;
  onStepClick: (step: PolicyFormStep) => void;
  /** Number of conditions with non-empty attribute, operator, and value */
  conditionCount?: number;
  /** Valid owner count by role (for step 2 subtitle) */
  businessOwnerCount?: number;
  technicalOwnerCount?: number;
  /** Which trigger has been selected (null = not yet chosen) */
  selectedTrigger?: TriggerType | null;
}

export default function PolicyFormSteps({
  selectedStep,
  onStepClick,
  conditionCount = 0,
  businessOwnerCount = 0,
  technicalOwnerCount = 0,
  selectedTrigger = null,
}: PolicyFormStepsProps) {
  const validOwnerCount = businessOwnerCount + technicalOwnerCount;
  const intl = useIntl();
  const isStep1Selected = selectedStep === 1;
  const isStep2Selected = selectedStep === 2;
  const triggerLabel = selectedTrigger ? TRIGGER_LABELS[selectedTrigger] : null;
  const step1Subtitle = triggerLabel
    ? intl.formatMessage(
        {
          id: 'B3y2Bc',
          defaultMessage:
            '{triggerLabel} • {count} {count, plural, one {condition} other {conditions}}',
        },
        { triggerLabel, count: conditionCount }
      )
    : null;
  const hasNoValidConditions = !selectedTrigger || conditionCount === 0;

  const stepBtnClass = (isSelected: boolean) =>
    cx(
      'PolicyForm-step-btn',
      isSelected
        ? 'PolicyForm-step-btn--isActive'
        : 'PolicyForm-step-btn--isInactive'
    );

  return (
    <Box className='PolicyForm-steps'>
      <Button
        kind='ghost'
        className={stepBtnClass(isStep1Selected)}
        onClick={() => onStepClick(1)}>
        <Box className='PolicyForm-stepBtnRow'>
          <Badge
            badgeStyle='outlined'
            kind='number'
            size='medium'
            number={1}
            className='PolicyForm-stepBtnBadge'
          />
          <Box className='PolicyForm-stepBtnTextWrap'>
            <Typography
              kind='body1'
              color={TOKENS.CF_NEU_BLD}
              className='PolicyForm-stepBtnTitle'>
              <FormattedMessage
                id='/pyDv6'
                defaultMessage='When should this rule run?'
              />
            </Typography>
            <Typography
              kind='body2'
              color={
                hasNoValidConditions ? TOKENS.CF_WRN_BLD : TOKENS.CF_SEC_BLD
              }
              className='PolicyForm-stepBtnSubtitle'>
              {hasNoValidConditions ? (
                <FormattedMessage id='kHwKVg' defaultMessage='Incomplete' />
              ) : (
                (step1Subtitle ?? (
                  <FormattedMessage id='kHwKVg' defaultMessage='Incomplete' />
                ))
              )}
            </Typography>
          </Box>
        </Box>
      </Button>
      <Button
        kind='ghost'
        className={stepBtnClass(isStep2Selected)}
        onClick={() => onStepClick(2)}>
        <Box className='PolicyForm-stepBtnRow'>
          <Badge
            badgeStyle='outlined'
            kind='number'
            size='medium'
            number={2}
            className='PolicyForm-stepBtnBadge'
          />
          <Box className='PolicyForm-stepBtnTextWrap'>
            <Typography
              kind='body1'
              color={TOKENS.CF_NEU_BLD}
              className='PolicyForm-stepBtnTitle'>
              <FormattedMessage
                id='jV+Qqd'
                defaultMessage='Who should be assigned as owners?'
              />
            </Typography>
            <Typography
              kind='body2'
              color={
                validOwnerCount > 0 ? TOKENS.CF_SEC_BLD : TOKENS.CF_WRN_BLD
              }
              className='PolicyForm-stepBtnSubtitle'>
              {validOwnerCount > 0 ? (
                <>
                  {businessOwnerCount > 0 && (
                    <FormattedMessage
                      id='YQ7m6X'
                      defaultMessage='{count} Business {count, plural, one {Owner} other {Owners}}'
                      description='Step 2 subtitle: Business owner count'
                      values={{ count: businessOwnerCount }}
                    />
                  )}
                  {businessOwnerCount > 0 &&
                    technicalOwnerCount > 0 &&
                    intl.formatMessage({
                      id: '/XEE2t',
                      defaultMessage: ' and ',
                      description:
                        'Conjunction between business and technical owner counts',
                    })}
                  {technicalOwnerCount > 0 && (
                    <FormattedMessage
                      id='k3UOsF'
                      defaultMessage='{count} Technical {count, plural, one {Owner} other {Owners}}'
                      description='Step 2 subtitle: Technical owner count'
                      values={{ count: technicalOwnerCount }}
                    />
                  )}
                </>
              ) : (
                <FormattedMessage id='kHwKVg' defaultMessage='Incomplete' />
              )}
            </Typography>
          </Box>
        </Box>
      </Button>
    </Box>
  );
}
